// Report analytics — pure RPC layer (no React, no hooks, no JSX).

import { BACKEND_URL } from '@/lib/constants/api';
import { apiFetch } from '@/lib/utils/apiFetch';
import type {
  IAuditDrilldownBody,
  IAuditDrilldownResponse,
  IAuditImpactAreasResponse,
  IAuditMetrics,
  IAuditUrgenciesResponse,
  ICertByStandardMetrics,
  ICertDrilldownBody,
  ICertDrilldownResponse,
  ICertMetrics,
  IExportPayload,
  ITracingDepthCompletion,
  ITracingMetrics,
  ITracingPoDocumentsResponse,
} from './types';

// ── Tracing Operations ────────────────────────────────────────────────────────

/** `GET /api/dashboard-analytics/tracing-metrics/:companyId`, in the data envelope. */
export function fetchTracingMetrics(
  companyId: string,
  filter: string,
): Promise<{ data: ITracingMetrics }> {
  return apiFetch<{ data: ITracingMetrics }>(
    `/dashboard-analytics/tracing-metrics/${companyId}?filter=${encodeURIComponent(filter)}`,
    {},
    BACKEND_URL,
  );
}

/** `GET /api/dashboard-analytics/tracing-documents/completion/:companyId`, in the data envelope. */
export function fetchTracingDepthCompletion(
  companyId: string,
  filter: string,
): Promise<{ data: ITracingDepthCompletion[] }> {
  return apiFetch<{ data: ITracingDepthCompletion[] }>(
    `/dashboard-analytics/tracing-documents/completion/${companyId}?filter=${encodeURIComponent(filter)}`,
    {},
    BACKEND_URL,
  );
}

/** `GET /api/dashboard-analytics/tracing-documents/:companyId`, in the paginated data envelope. */
export function fetchTracingPoDocuments(
  companyId: string,
  qs: string,
): Promise<ITracingPoDocumentsResponse> {
  return apiFetch<ITracingPoDocumentsResponse>(
    `/dashboard-analytics/tracing-documents/${companyId}?${qs}`,
    {},
    BACKEND_URL,
  );
}

/** `POST /api/dashboard-analytics/tracing-metrics/export`, answering 204. */
export function exportTracingMetrics(body: IExportPayload): Promise<void> {
  return apiFetch<void>(
    '/dashboard-analytics/tracing-metrics/export',
    { method: 'POST', body: JSON.stringify(body) },
    BACKEND_URL,
  );
}

// ── Certificate Compliance ────────────────────────────────────────────────────

/** `GET /api/dashboard-analytics/certification-metrics/:companyId`, in the data envelope. */
export function fetchCertMetrics(
  companyId: string,
  filter: string,
): Promise<{ data: ICertMetrics }> {
  return apiFetch<{ data: ICertMetrics }>(
    `/dashboard-analytics/certification-metrics/${companyId}?filter=${encodeURIComponent(filter)}`,
    {},
    BACKEND_URL,
  );
}

/** `GET /api/dashboard-analytics/certification-metrics/by-standard/:companyId`, in the data envelope. */
export function fetchCertByStandardMetrics(
  companyId: string,
  filter: string,
): Promise<{ data: ICertByStandardMetrics }> {
  return apiFetch<{ data: ICertByStandardMetrics }>(
    `/dashboard-analytics/certification-metrics/by-standard/${companyId}?filter=${encodeURIComponent(filter)}`,
    {},
    BACKEND_URL,
  );
}

export function fetchCertDrilldown(
  body: ICertDrilldownBody,
): Promise<ICertDrilldownResponse> {
  return apiFetch<ICertDrilldownResponse>(
    '/dashboard-analytics/certification-drilldown/list',
    { method: 'POST', body: JSON.stringify(body) },
    BACKEND_URL,
  );
}

/** `POST /api/dashboard-analytics/certification-metrics/export`, answering 204. */
export function exportCertMetrics(filter: string): Promise<void> {
  return apiFetch<void>(
    '/dashboard-analytics/certification-metrics/export',
    { method: 'POST', body: JSON.stringify({ filter }) },
    BACKEND_URL,
  );
}

// ── Audit Compliance ─────────────────────────────────────────────────────────

/** `GET /api/dashboard-analytics/audits-metrics/:companyId`, in the data envelope. */
export function fetchAuditMetrics(
  companyId: string,
  filter: string,
): Promise<{ data: IAuditMetrics }> {
  return apiFetch<{ data: IAuditMetrics }>(
    `/dashboard-analytics/audits-metrics/${companyId}?filter=${encodeURIComponent(filter)}`,
    {},
    BACKEND_URL,
  );
}

/** `GET /api/dashboard-analytics/audits-metrics/urgencies/:companyId`, in the data envelope. */
export function fetchAuditUrgencies(
  companyId: string,
  filter: string,
): Promise<{ data: IAuditUrgenciesResponse }> {
  return apiFetch<{ data: IAuditUrgenciesResponse }>(
    `/dashboard-analytics/audits-metrics/urgencies/${companyId}?filter=${encodeURIComponent(filter)}`,
    {},
    BACKEND_URL,
  );
}

/** `GET /api/dashboard-analytics/audits-metrics/impact-areas/:companyId`, paginated. */
export function fetchAuditImpactAreas(
  companyId: string,
  qs: string,
): Promise<IAuditImpactAreasResponse> {
  return apiFetch<IAuditImpactAreasResponse>(
    `/dashboard-analytics/audits-metrics/impact-areas/${companyId}?${qs}`,
    {},
    BACKEND_URL,
  );
}

export function fetchAuditDrilldown(
  body: IAuditDrilldownBody,
): Promise<IAuditDrilldownResponse> {
  return apiFetch<IAuditDrilldownResponse>(
    '/dashboard-analytics/audits-metrics/drill-down/list',
    { method: 'POST', body: JSON.stringify(body) },
    BACKEND_URL,
  );
}
